import axios from 'axios';

// Get the auth token from localStorage/sessionStorage
const getToken = () => {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
};

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.miplapp.in/public/api';

// Create axios instance with auth header
const createApiInstance = () => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    timeout: 30000, // 30 second timeout for large HTML files
  });

  // Request interceptor to add token
  instance.interceptors.request.use(
    (config) => {
      const token = getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      console.log('API Request:', config.method.toUpperCase(), config.url);
      
      // Log request body size for debugging (don't log full content)
      if (config.data && typeof config.data === 'object') {
        const messageLength = config.data.message?.length || 0;
        console.log(`Request body size: ~${JSON.stringify(config.data).length} chars, message: ${messageLength} chars`);
      }
      
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor for error handling
  instance.interceptors.response.use(
    (response) => {
      console.log('API Response:', response.status, response.config.url);
      return response;
    },
    (error) => {
      console.error('API Error Details:', {
        status: error.response?.status,
        url: error.config?.url,
        message: error.message,
        response_data: error.response?.data
      });
      
      if (error.response?.status === 401) {
        // Dispatch custom event for auth context to handle
        if (typeof window !== 'undefined') {
          const event = new CustomEvent('forceLogout', { 
            detail: { 
              message: 'Your session has expired',
              passwordChanged: error.response.data?.password_changed || false
            } 
          });
          window.dispatchEvent(event);
        }
      }
      
      // Enhance error message for better debugging
      if (error.response?.data?.message) {
        error.message = error.response.data.message;
      }
      
      return Promise.reject(error);
    }
  );

  return instance;
};

// ========================================
// EMAIL MARKETING API FUNCTIONS
// All endpoints need the /email prefix as per your routes
// ========================================

// Stats
export const getStats = async () => {
  const api = createApiInstance();
  const response = await api.get('/email/stats');
  return response.data;
};

// Groups & Batches
export const getGroups = async () => {
  const api = createApiInstance();
  const response = await api.get('/email/groups');
  return response.data;
};

export const getBatches = async (group) => {
  const api = createApiInstance();
  const response = await api.get(`/email/batches?group=${encodeURIComponent(group)}`);
  return response.data;
};

export const getDivisions = async (group) => {
  const api = createApiInstance();
  const response = await api.get(`/email/divisions?group=${encodeURIComponent(group)}`);
  return response.data;
};

// Campaigns
export const getCampaigns = async (page = 1, filters = {}) => {
  const api = createApiInstance();
  
  const params = {
    page: page
  };
  
  if (filters.status && filters.status.trim() !== '') {
    params.status = filters.status;
  }
  
  if (filters.search && filters.search.trim() !== '') {
    params.search = filters.search;
  }
  
  if (filters.per_page) {
    params.per_page = filters.per_page;
  }
  
  const queryString = new URLSearchParams(params).toString();
  const url = queryString ? `/email/campaigns?${queryString}` : '/email/campaigns';
  
  console.log('Fetching campaigns with params:', params);
  const response = await api.get(url);
  return response.data;
};

export const getCampaign = async (id) => {
  const api = createApiInstance();
  const response = await api.get(`/email/campaigns/${id}`);
  return response.data;
};

export const createCampaign = async (data) => {
  const api = createApiInstance();
  
  // Validate and prepare the data
  const payload = {
    ...data,
    message: String(data.message || ''), // Ensure message is a string
    include_demo_section: Boolean(data.include_demo_section),
    selected_divisions: data.selected_divisions || [],
    selected_batches: data.selected_batches || []
  };
  
  // Check if message contains HTML for logging
  const isHtml = /<[a-z][\s\S]*>/i.test(payload.message);
  
  console.log('Creating campaign:', {
    subject: payload.subject,
    message_length: payload.message.length,
    message_type: isHtml ? 'HTML' : 'text',
    source_collection: payload.source_collection,
    selected_divisions_count: payload.selected_divisions.length,
    image_url: payload.image_url ? 'present' : 'none'
  });
  
  try {
    const response = await api.post('/email/campaigns', payload);
    return response.data;
  } catch (error) {
    console.error('Create campaign failed:', {
      status: error.response?.status,
      message: error.response?.data?.message,
      errors: error.response?.data?.errors
    });
    throw error;
  }
};

export const sendCampaign = async (id) => {
  const api = createApiInstance();
  const response = await api.post(`/email/campaigns/${id}/send`);
  return response.data;
};

export const getCampaignStats = async (id) => {
  const api = createApiInstance();
  const response = await api.get(`/email/campaigns/${id}/stats`);
  return response.data;
};

export const getCampaignLogs = async (id, page = 1, perPage = 500, filter = '') => {
  const api = createApiInstance();
  const params = new URLSearchParams({ page, per_page: perPage });
  if (filter) params.append('filter', filter);
  const response = await api.get(`/email/campaigns/${id}/logs?${params.toString()}`);
  return response.data;
};

export const deleteCampaign = async (id) => {
  const api = createApiInstance();
  const response = await api.delete(`/email/campaigns/${id}`);
  return response.data;
};

// Image Upload
export const uploadImage = async (file) => {
  const api = createApiInstance();
  const formData = new FormData();
  formData.append('image', file);
  
  console.log('Uploading image:', file.name, file.size, file.type);
  
  const response = await api.post('/email/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000, // 60 seconds for image upload
  });
  
  // Return just the filename
  return response.data.filename;
};

// Customers
export const getCustomers = async (params = {}) => {
  const api = createApiInstance();
  const qs = new URLSearchParams(params).toString();
  const response = await api.get(`/email/customers?${qs}`);
  return response.data;
};

export const importCustomers = async (data) => {
  const api = createApiInstance();
  const response = await api.post('/email/customers/import', data);
  return response.data;
};

export const deleteCustomer = async (id) => {
  const api = createApiInstance();
  const response = await api.delete(`/email/customers/${id}`);
  return response.data;
};

export const getCustomerGroups = async () => {
  const api = createApiInstance();
  const response = await api.get('/email/customers/groups');
  return response.data;
};

export const getCustomerBatches = async (group) => {
  const api = createApiInstance();
  const response = await api.get(`/email/customers/batches/${encodeURIComponent(group)}`);
  return response.data;
};

// ========================================
// PROMOTION FILTER API FUNCTIONS
// ========================================

export const getUniquePromotions = async () => {
  const api = createApiInstance();
  try {
    const response = await api.get('/email/promotions');
    return response.data;
  } catch (error) {
    console.error('Error fetching promotions:', error);
    return { data: [] };
  }
};

export const getPromotionStats = async (promotion) => {
  const api = createApiInstance();
  try {
    const response = await api.get(`/email/promotions/${encodeURIComponent(promotion)}/stats`);
    return response.data;
  } catch (error) {
    console.error('Error fetching promotion stats:', error);
    return { 
      data: {
        total_emails: 0,
        unique_opens: 0,
        unique_clicks: 0,
        open_rate: 0,
        click_rate: 0
      } 
    };
  }
};

export const getLogsByPromotion = async (promotion, page = 1, perPage = 1000) => {
  const api = createApiInstance();
  try {
    const params = new URLSearchParams({ page, per_page: perPage });
    const response = await api.get(`/email/logs/by-promotion/${encodeURIComponent(promotion)}?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching logs by promotion:', error);
    return { 
      data: {
        data: [],
        current_page: 1,
        last_page: 1
      } 
    };
  }
};

export const getCampaignLogsByPromotion = async (promotion, page = 1, perPage = 1000) => {
  const api = createApiInstance();
  try {
    const params = new URLSearchParams({ 
      page, 
      per_page: perPage,
      promotion: promotion 
    });
    const response = await api.get(`/email/campaign-logs?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching campaign logs by promotion:', error);
    return { 
      data: {
        data: [],
        current_page: 1,
        last_page: 1
      } 
    };
  }
};

// ========================================
// AI GENERATION — Claude (Anthropic)
// ========================================

export const generateCampaignContent = async ({ brief, campaignType, tone, brandName }) => {
  const api = createApiInstance();
  const response = await api.post('/email/ai/generate-campaign', {
    brief,
    campaign_type: campaignType,
    tone: tone || 'professional',
    brand_name: brandName || '',
  });
  return response.data;
};

// ========================================
// DEMO BOOKINGS API FUNCTIONS
// ========================================

export const getDemoBookings = async (params = {}) => {
  const api = createApiInstance();
  const qs = new URLSearchParams(params).toString();
  const response = await api.get(`/email/demo-bookings${qs ? `?${qs}` : ''}`);
  return response.data;
};

export const getDemoBookingStats = async () => {
  const api = createApiInstance();
  const response = await api.get('/email/demo-bookings/stats');
  return response.data;
};

export const updateDemoBooking = async (id, data) => {
  const api = createApiInstance();
  const response = await api.patch(`/email/demo-bookings/${id}`, data);
  return response.data;
};

// ========================================
// TRACKING ROUTES (Public - no auth needed)
// ========================================

export const trackOpen = async (email, campaignCode) => {
  const api = axios.create({ baseURL: API_BASE_URL });
  const response = await api.get(`/track/open?email=${encodeURIComponent(email)}&campaign=${campaignCode}`);
  return response.data;
};

export const trackClick = async (email, campaignCode, url) => {
  const api = axios.create({ baseURL: API_BASE_URL });
  const response = await api.get(`/track/click?email=${encodeURIComponent(email)}&campaign=${campaignCode}&url=${encodeURIComponent(url)}`);
  return response.data;
};

// ========================================
// UTILITY FUNCTIONS
// ========================================

// Helper function to validate campaign data before sending
export const validateCampaignData = (campaignData) => {
  const errors = [];
  
  if (!campaignData.subject || campaignData.subject.trim() === '') {
    errors.push('Subject is required');
  }
  
  if (!campaignData.message || campaignData.message.trim() === '') {
    errors.push('Message is required');
  }
  
  if (!campaignData.source_collection) {
    errors.push('Customer group is required');
  }
  
  if (!campaignData.selected_divisions || campaignData.selected_divisions.length === 0) {
    errors.push('At least one division is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Helper function to format campaign data for API
export const formatCampaignData = (formData, brandData) => {
  return {
    subject: formData.subject.trim(),
    message: formData.message,
    image_url: formData.image_url || null,
    source_collection: formData.source_collection,
    selected_batches: formData.selected_batches || [],
    selected_divisions: formData.selected_divisions || [],
    include_demo_section: formData.include_demo_section,
    website_url: formData.website_url?.trim() || 'https://www.ottomed.com/',
    brand_name: brandData.brandName || '',
  };
};

// Export default object for convenience
export default {
  getStats,
  getGroups,
  getBatches,
  getDivisions,
  getCampaigns,
  getCampaign,
  createCampaign,
  sendCampaign,
  getCampaignStats,
  getCampaignLogs,
  deleteCampaign,
  uploadImage,
  getCustomers,
  importCustomers,
  deleteCustomer,
  getCustomerGroups,
  getCustomerBatches,
  getUniquePromotions,
  getPromotionStats,
  getLogsByPromotion,
  getCampaignLogsByPromotion,
  generateCampaignContent,
  getDemoBookings,
  getDemoBookingStats,
  updateDemoBooking,
  trackOpen,
  trackClick,
  validateCampaignData,
  formatCampaignData
};